import {
  type RoomNameMatcherOptions,
  type RoomNameRatePlanQualifier,
  detectRatePlanQualifier,
  findMatchingRoomNames,
  findMostSimilarRoomName,
  isAllRoomsName,
  stripRoomNameNoise,
} from '../../shared/roomNameMatcher';

import { UNKNOWN_RATE_PLAN_NAME } from './egsRatePlans';

const EGS_MATCHER_OPTIONS: RoomNameMatcherOptions = { plurals: true };

// A score above this is shown to a CM for confirmation, never treated as a match.
export const EGS_SUGGESTION_MIN_SCORE = 0.5;

// Only `matched` carries `roomRateIds`, so nothing else can be applied by accident. "All Rooms"
// describes what the CM negotiated; it is not an instruction to surcharge every room rate.
export type EgsRoomMatch =
  | { matchedRoomTypes: Array<App.RoomType>; roomRateIds: Array<string>; source: 'matched' }
  | {
      /** Empty means we could not narrow the rooms, so every room rate is offered rather than none. */
      matchedRoomTypes: Array<App.RoomType>;
      qualifier: RoomNameRatePlanQualifier;
      qualifierText: string;
      source: 'rate_plan_qualified';
    }
  | { source: 'all_rooms' }
  | { source: 'suggested' }
  | { source: 'unmatched' };

export function getRoomRateIds(roomTypes: Array<App.RoomType>): Array<string> {
  return roomTypes.flatMap((roomType) => (roomType.room_rates ?? []).map((roomRate) => String(roomRate.id)));
}

function narrowRatePlanQualifiedRoomTypes(
  roomLabel: string,
  qualifierText: string,
  roomTypes: Array<App.RoomType>,
): Array<App.RoomType> {
  const candidateText = stripRoomNameNoise(roomLabel.replace(qualifierText, ' '));

  if (!candidateText) {
    return [];
  }

  const matchedNames = new Set(
    findMatchingRoomNames(
      candidateText,
      roomTypes.map((roomType) => roomType.name),
      EGS_MATCHER_OPTIONS,
    ),
  );

  return roomTypes.filter((roomType) => matchedNames.has(roomType.name));
}

export function matchEgsRoomLabel(roomLabel: string, roomTypes: Array<App.RoomType>): EgsRoomMatch {
  // Ahead of All Rooms: a board basis prices one rate plan, and every other verdict writes to all of them.
  const ratePlan = detectRatePlanQualifier(roomLabel);

  if (ratePlan) {
    return {
      matchedRoomTypes: narrowRatePlanQualifiedRoomTypes(roomLabel, ratePlan.text, roomTypes),
      qualifier: ratePlan.qualifier,
      qualifierText: ratePlan.text,
      source: 'rate_plan_qualified',
    };
  }

  // Before noise stripping, which would delete a parenthesised exclusion.
  if (isAllRoomsName(roomLabel)) {
    return { source: 'all_rooms' };
  }

  const candidateText = stripRoomNameNoise(roomLabel);

  if (!candidateText) {
    return { source: 'unmatched' };
  }

  const roomTypeNames = roomTypes.map((roomType) => roomType.name);
  const matchedNames = new Set(findMatchingRoomNames(candidateText, roomTypeNames, EGS_MATCHER_OPTIONS));

  if (matchedNames.size) {
    const matchedRoomTypes = roomTypes.filter((roomType) => matchedNames.has(roomType.name));

    return { matchedRoomTypes, roomRateIds: getRoomRateIds(matchedRoomTypes), source: 'matched' };
  }

  // Changes how the row reads, never what it offers: both verdicts open every room unticked.
  const suggestion = findMostSimilarRoomName(candidateText, roomTypeNames, EGS_MATCHER_OPTIONS);

  if (suggestion && suggestion.score >= EGS_SUGGESTION_MIN_SCORE) {
    return { source: 'suggested' };
  }

  return { source: 'unmatched' };
}

/**
 * Makes a new verdict a type error at every call site that forgot it, which `strictNullChecks` being off
 * otherwise allows. Returns the fallback rather than throwing, so a missed verdict renders a wrong label
 * instead of taking the tab down.
 */
export function unhandledEgsMatch<T>(match: never, fallback: T): T {
  return fallback;
}

// Which rooms a verdict reaches, derived here so no two readers of a verdict can disagree.
export interface EgsMatchScope {
  /** True where the room types are what the record says, so they can be counted and pre-ticked. */
  isResolved: boolean;
  /**
   * Not a boolean: an `ambiguous` shortlist stops being a question once something narrows it to one room,
   * while a `confirm` is a reading of the name that no amount of narrowing makes safe to pre-tick.
   */
  pickReason?: 'ambiguous' | 'confirm';
  /** The board basis the record named. Present only on a rate-plan-qualified verdict. */
  ratePlanQualifier?: string;
  /** `every` where the record narrowed nothing, so the whole grid is reached rather than none of it. */
  roomTypeIds: Set<string> | 'every';
}

function toRoomTypeIds(roomTypes: Array<App.RoomType>): Set<string> {
  return new Set(roomTypes.map((roomType) => String(roomType.id)));
}

/**
 * Every room, ticked none. A name we could not read is a name we have nothing to say about, and the CM can
 * already write the surcharge by hand in the room rate editor, so the matcher does not get to refuse them.
 */
function unmatchedScope(): EgsMatchScope {
  return { isResolved: false, pickReason: 'confirm', roomTypeIds: 'every' };
}

// A verdict this switch never handled is one whose rooms nobody has worked out, so it offers none. Built per
// call rather than shared, so no caller can widen one verdict's rooms by mutating another's set.
function unhandledScope(): EgsMatchScope {
  return { isResolved: false, roomTypeIds: new Set() };
}

export function getEgsMatchScope(match: EgsRoomMatch): EgsMatchScope {
  switch (match.source) {
    case 'all_rooms':
      return { isResolved: true, roomTypeIds: 'every' };

    case 'rate_plan_qualified':
      return {
        isResolved: false,
        pickReason: 'confirm',
        ratePlanQualifier: match.qualifierText,
        roomTypeIds: match.matchedRoomTypes.length ? toRoomTypeIds(match.matchedRoomTypes) : 'every',
      };

    // Always carries a count. Several room types is the ambiguous pick: the name is right and only the room
    // is open, so scoping down to one settles it without a human.
    case 'matched':
      return {
        isResolved: true,
        pickReason: match.matchedRoomTypes.length > 1 ? 'ambiguous' : undefined,
        roomTypeIds: toRoomTypeIds(match.matchedRoomTypes),
      };

    // A prompt, not a match: every room is reached, and none of them is counted.
    case 'suggested':
      return { isResolved: false, pickReason: 'confirm', roomTypeIds: 'every' };

    case 'unmatched':
      return unmatchedScope();

    default:
      return unhandledEgsMatch(match, unhandledScope());
  }
}

export type EgsRoomDataState = 'error' | 'loading' | 'unavailable' | 'ready';

export function getRoomDataState({
  error,
  hasRoomTypes,
  isLoading,
}: {
  error: unknown;
  hasRoomTypes: boolean;
  isLoading: boolean;
}): EgsRoomDataState {
  if (error) {
    return 'error';
  }

  if (isLoading) {
    return 'loading';
  }

  return hasRoomTypes ? 'ready' : 'unavailable';
}

// Null, not an empty list: the caller turns a list into a count, and an empty one would read as a real zero.
export function getMatchRoomRateIds(match: EgsRoomMatch, roomTypes: Array<App.RoomType>): Array<string> | null {
  const { isResolved, roomTypeIds } = getEgsMatchScope(match);

  if (!isResolved) {
    return null;
  }

  const reached =
    roomTypeIds === 'every' ? roomTypes : roomTypes.filter((roomType) => roomTypeIds.has(String(roomType.id)));

  return getRoomRateIds(reached);
}

export function getMatchRoomRateCount(match: EgsRoomMatch, roomTypes: Array<App.RoomType>): number | null {
  return getMatchRoomRateIds(match, roomTypes)?.length ?? null;
}

// One count per room label, which is one row of the table, so the count and the room rate list agree.
export function countUnresolvedMatches(matchByRoomLabel: Record<string, EgsRoomMatch>): number {
  return Object.values(matchByRoomLabel).filter((match) => !getEgsMatchScope(match).isResolved).length;
}

export function getEgsRoomRateCount({
  error,
  hasRoomTypes,
  isLoading,
  match,
  roomTypes,
}: {
  error: unknown;
  hasRoomTypes: boolean;
  isLoading: boolean;
  match: EgsRoomMatch;
  roomTypes: Array<App.RoomType>;
}): number | null {
  const roomDataState = getRoomDataState({ error, hasRoomTypes, isLoading });
  return roomDataState === 'ready' ? getMatchRoomRateCount(match, roomTypes) : null;
}

// Mirrors EgsMatchStatusChip's label text, so the Status column can sort on the same words it
// renders. Null means the chip shows a placeholder (loading/error/unavailable) rather than a word.
export function getEgsMatchStatusLabel({
  error,
  hasRoomTypes,
  isLoading,
  match,
}: {
  error: unknown;
  hasRoomTypes: boolean;
  isLoading: boolean;
  match: EgsRoomMatch;
}): string | null {
  const roomDataState = getRoomDataState({ error, hasRoomTypes, isLoading });

  if (roomDataState === 'error' || roomDataState === 'loading') {
    return null;
  }

  if (match.source === 'rate_plan_qualified') {
    return 'Pick rate plan';
  }

  if (match.source === 'all_rooms') {
    return 'All rooms';
  }

  if (roomDataState === 'unavailable') {
    return null;
  }

  switch (match.source) {
    case 'matched':
      return match.matchedRoomTypes.length > 1 ? 'Pick room' : 'Matched';

    case 'suggested':
      return 'Pick room';

    case 'unmatched':
      return 'No match';

    default:
      return unhandledEgsMatch(match, 'No match');
  }
}

export type EgsMatchStatusColor = 'default' | 'info' | 'success' | 'warning';

export function getEgsMatchStatusColor(match: EgsRoomMatch): EgsMatchStatusColor {
  switch (match.source) {
    case 'rate_plan_qualified':
    case 'suggested':
      return 'warning';

    case 'all_rooms':
      return 'info';

    case 'matched':
      return match.matchedRoomTypes.length > 1 ? 'warning' : 'success';

    default:
      return 'default';
  }
}

export function buildEgsMatchByRoomLabel(
  roomLabels: Array<string>,
  roomTypes: Array<App.RoomType>,
): Record<string, EgsRoomMatch> {
  return Object.fromEntries(
    Array.from(new Set(roomLabels)).map((roomLabel) => [roomLabel, matchEgsRoomLabel(roomLabel, roomTypes)]),
  );
}

export interface EgsRoomRateGroupItem {
  ratePlanName: string;
  roomRateId: string;
}

export interface EgsRoomRateGroup {
  roomRates: Array<EgsRoomRateGroupItem>;
  roomTypeId: string;
  roomTypeName: string;
}

// Carries ids alongside names because neither a rate plan name nor a room type name is unique, so
// only the ids are safe to key a list on.
export function buildRoomRateGroups(
  roomRateIds: Array<string>,
  roomTypes: Array<App.RoomType>,
): Array<EgsRoomRateGroup> {
  const wanted = new Set(roomRateIds);

  return roomTypes
    .map((roomType) => ({
      roomRates: (roomType.room_rates ?? [])
        .filter((roomRate) => wanted.has(String(roomRate.id)))
        .map((roomRate) => ({
          ratePlanName: roomRate.rate_plan?.name ?? UNKNOWN_RATE_PLAN_NAME,
          roomRateId: String(roomRate.id),
        })),
      roomTypeId: String(roomType.id),
      roomTypeName: roomType.name,
    }))
    .filter((group) => group.roomRates.length > 0);
}

export function getUnmatchedRoomRateIds({
  egsByRoomRateId,
  matchByRoomLabel,
  roomTypes,
}: {
  egsByRoomRateId: Record<string, Array<App.ExtraGuestSurcharges>>;
  matchByRoomLabel: Record<string, EgsRoomMatch>;
  roomTypes: Array<App.RoomType>;
}): Array<string> {
  // A near miss reaches every room without naming one, so counting reach as cover would empty this on a guess.
  const coveredRoomRateIds = new Set(
    Object.values(matchByRoomLabel).flatMap((match) => getMatchRoomRateIds(match, roomTypes) ?? []),
  );

  return Object.entries(egsByRoomRateId)
    .filter(([roomRateId, records]) => records.length > 0 && !coveredRoomRateIds.has(roomRateId))
    .map(([roomRateId]) => roomRateId);
}
